import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import {
  CustomDialogProps,
  SmartForm,
  SmartSubmit,
  SmartTextField,
  useSmartForm,
} from "@nodeon/react-components";
import { useSnackbar } from "notistack";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiResponse,
  SurveyMethodListDto,
  useSurveyMethodApi,
} from "~/api/GeneratedApi";

interface FormProps {
  name: string;
}

interface Props {
  surveyMethod?: SurveyMethodListDto;
  onSubmitted?: (name: string) => void;
  initialValue?: string;
}

const SurveyMethodDialog = ({
  handleClose,
  onSubmitted,
  surveyMethod,
  initialValue,
}: CustomDialogProps & Props): JSX.Element => {
  const { editSurveyMethod, addSurveyMethod } = useSurveyMethodApi();
  const { t } = useTranslation("survey-method");
  const { enqueueSnackbar } = useSnackbar();

  const edit = useMemo(() => !!surveyMethod, [surveyMethod]);

  const onSuccess = (data: FormProps, res: ApiResponse) => {
    enqueueSnackbar(t(`apiResponses:${res.resultKey}`), { variant: "success" });
    onSubmitted?.(data.name);
    handleClose?.();
  };

  const onSubmit = (data: FormProps) => {
    return edit
      ? editSurveyMethod(
          {
            name: data.name,
          },
          surveyMethod!.id,
        )
      : addSurveyMethod({
          name: data.name,
        });
  };

  const defaultValues = useMemo(
    () => ({ name: surveyMethod?.name ?? initialValue ?? "" }),
    [initialValue, surveyMethod?.name],
  );

  const formMethods = useSmartForm<FormProps, ApiResponse>({
    defaultValues,
  });

  return (
    <Dialog open onClose={handleClose}>
      <SmartForm<FormProps, ApiResponse>
        onSubmit={onSubmit}
        noGrid
        onSuccess={(data, res) => onSuccess(data, res)}
        methods={formMethods}
      >
        <DialogTitle id="form-dialog-title">
          {edit ? t("edit-title") : t("create-title")}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SmartTextField<FormProps>
                fullWidth
                name="name"
                label={t("name")}
                required
                autoFocus
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {t("general:cancel")}
          </Button>
          <SmartSubmit disabled={edit ? !formMethods.formState.isDirty : false}>
            {edit ? t("general:edit") : t("general:create")}
          </SmartSubmit>
        </DialogActions>
      </SmartForm>
    </Dialog>
  );
};

export default SurveyMethodDialog;
